import type { Pair } from '../messages';
import { ruPlural } from '../plural';

/** The collection editor: block list, drag-and-drop announcements, the meta aside and the picker. */
export const editor = {
  'editor.lockedTitle': ['Редактор недоступен', 'Editor unavailable'],
  'editor.lockedBody': ['Эту подборку создали не вы.', 'You did not create this collection.'],
  'editor.openPlaylist': ['Открыть подборку', 'Open collection'],
  'editor.titleRequired': ['Название не может быть пустым.', 'The title cannot be empty.'],
  'editor.saved': ['Сохранено', 'Saved'],
  'editor.itemAppended': [
    'Элемент добавлен в конец подборки',
    'Added to the end of the collection',
  ],
  'editor.deleted': ['Подборка удалена', 'Collection deleted'],
  'editor.blockWord': ['блок', 'block'],
  'editor.fallbackItem': ['элемент', 'item'],
  'editor.fallbackNote': ['заметка', 'note'],
  'editor.blockLabel': [
    (v) => `«${v.name}», место ${v.n}`,
    (v) => `“${v.name}”, position ${v.n}`,
  ],
  'editor.previewLink': ['← Как это видят другие', '← How others see it'],
  'editor.heading': ['Редактор подборки', 'Collection editor'],
  'editor.blocksCount': [
    (v) => ruPlural(+v.n, 'блок', 'блока', 'блоков'),
    (v) => (+v.n === 1 ? '1 block' : `${v.n} blocks`),
  ],
  'editor.notesSuffix': [
    (v) => ` (заметок: ${v.n})`,
    (v) => (+v.n === 1 ? ' (1 note)' : ` (${v.n} notes)`),
  ],
  'editor.dndStart': [
    (v) => `Взяли ${v.label}. Стрелками двигайте, пробросом отпускайте.`,
    (v) => `Picked up ${v.label}. Use the arrows to move, the space bar to drop.`,
  ],
  'editor.dndOver': [(v) => `${v.label} встанет на место ${v.n}`, (v) => `${v.label} takes position ${v.n}`],
  'editor.dndEnd': [(v) => `${v.label} → место ${v.n}`, (v) => `${v.label} → position ${v.n}`],
  'editor.dndUnchanged': ['Порядок не изменился.', 'Order unchanged.'],
  'editor.dndCancelled': ['Перестановка отменена, порядок прежний.', 'Move cancelled, order unchanged.'],
  'editor.noteLabel': ['Заметка', 'Note'],
  'editor.emptyBlocks': [
    'Пусто. Добавьте первую ссылку из библиотеки — порядок потом меняется перетаскиванием.',
    'Empty. Add the first link from your library — reorder it later by dragging.',
  ],
  'editor.gripHint': [
    'Держите за шесть точек слева, чтобы переставить. На телефоне — зажмите и ведите.',
    'Hold the six dots on the left to reorder. On a phone — press and drag.',
  ],
  'editor.addFromLibrary': ['Элемент из библиотеки', 'Item from the library'],
  'editor.addMyNote': ['Моя заметка', 'My note'],
  'editor.textField': ['Текст между элементами', 'Text between items'],
  'editor.textHint': [
    'Подпись, контекст, почему именно этот порядок.',
    'A caption, context, why this order.',
  ],
  'editor.textPlaceholder': [
    'Например: дальше — по хронологии событий, а не по дате выхода.',
    'For example: next by story order, not by release date.',
  ],
  'editor.appendToEnd': ['Добавить в конец', 'Add to the end'],
  'editor.coverLabel': ['Обложка подборки (URL)', 'Collection cover (URL)'],
  'editor.coverHint': ['Если пусто — подставим первую карточку.', 'If empty, we use the first card.'],
  'editor.visibility': ['Видимость', 'Visibility'],
  'editor.saveDescription': ['Сохранить описание', 'Save description'],
  'editor.allSaved': ['Всё сохранено', 'All saved'],
  'editor.deletePlaylist': ['Удалить подборку', 'Delete collection'],
  'editor.unsaved': ['Есть несохранённые правки', 'Unsaved changes'],
  'editor.orderSaved': ['Порядок сохранён', 'Order saved'],
  'editor.preview': ['Просмотр', 'Preview'],
  'editor.confirmTitle': ['Удалить подборку?', 'Delete this collection?'],
  'editor.confirmBody': [
    (v) => `«${v.title}» и её порядок будут удалены. Ссылки в библиотеке останутся.`,
    (v) => `“${v.title}” and its order will be deleted. Your links stay in the library.`,
  ],
  'editor.gripAria': [
    (v) => `Переставить блок ${v.n} из ${v.total}`,
    (v) => `Reorder block ${v.n} of ${v.total}`,
  ],
  'editor.noteTextareaAria': ['Текст заметки', 'Note text'],
  'editor.noteBlurHint': ['Сохранится, когда уберёте курсор.', 'Saves when you leave the field.'],
  'editor.emptyNote': ['Пустая заметка — нажмите, чтобы написать', 'Empty note — tap to write'],
  'editor.moveUp': ['Выше', 'Up'],
  'editor.moveDown': ['Ниже', 'Down'],
  'editor.removeBlock': ['Убрать из подборки', 'Remove from collection'],
  'editor.removeNote': ['Удалить заметку', 'Delete note'],
  'editor.pickerBody': [
    'Одна ссылка может жить в нескольких подборках — её карточка при этом остаётся одной.',
    'One link can live in several collections — its card stays a single one.',
  ],
  'editor.newSave': ['Новое сохранение', 'Save something new'],
  'editor.pickSearchAria': ['Поиск по библиотеке', 'Search your library'],
  'editor.pickSearchPlaceholder': ['Название или домен', 'Title or domain'],
  'editor.pickEmptyTitle': ['В библиотеке пусто', 'Your library is empty'],
  'editor.pickEmptyBody': [
    'Сначала сохраните ссылку — затем она появится здесь.',
    'Save a link first — it shows up here after.',
  ],
  'editor.alreadyHere': ['уже здесь', 'already here'],
} satisfies Record<string, Pair>;
